/**
 * Platform Administration API - Phase 68 M5
 *
 * API functions for maintenance mode and platform-level operations.
 * Endpoints: /api/admin/maintenance-status, /api/admin/maintenance-mode
 */
import axios from 'axios'
import toast from 'react-hot-toast'
import { bffBaseURL, normalizeError } from './apiClient'

// =============================================================================
// Types
// =============================================================================

export interface MaintenanceStatus {
  active: boolean
  message: string | null
  set_by: string | null
  set_at: string | null
  is_super_admin: boolean
}

export interface MaintenanceModeRequest {
  active: boolean
  message?: string | null
}

export interface MaintenanceModeResponse {
  success: boolean
  active: boolean
  message: string | null
  set_by: string | null
  set_at: string | null
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get current maintenance mode status.
 *
 * This is a public endpoint (no auth required).
 * Returns maintenance status + whether current user is super_admin.
 */
export async function getMaintenanceStatus(): Promise<MaintenanceStatus> {
  try {
    const response = await axios.get<MaintenanceStatus>(
      `${bffBaseURL}/api/admin/maintenance-status`,
      { withCredentials: true }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError(
        (error.response?.data as Record<string, unknown>)?.detail ||
        'Error al obtener estado de mantenimiento'
      )
      throw new Error(message)
    }
    throw error
  }
}

/**
 * Toggle maintenance mode on/off.
 *
 * Requires super_admin role.
 *
 * @param active - Whether to enable maintenance mode
 * @param message - Optional custom message to display
 */
export async function setMaintenanceMode(
  active: boolean,
  message?: string | null
): Promise<MaintenanceModeResponse> {
  try {
    const payload: MaintenanceModeRequest = { active }
    if (message) {
      payload.message = message
    }

    const response = await axios.post<MaintenanceModeResponse>(
      `${bffBaseURL}/api/admin/maintenance-mode`,
      payload,
      { withCredentials: true }
    )

    // Show success toast
    if (response.data.success) {
      toast.success(
        active
          ? 'Modo de mantenimiento activado'
          : 'Modo de mantenimiento desactivado'
      )
    }

    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError(
        (error.response?.data as Record<string, unknown>)?.detail ||
        'Error al configurar modo de mantenimiento'
      )
      toast.error(message)
      throw new Error(message)
    }
    throw error
  }
}
